/**
 * GlassSelect — drop-in replacement for native `<select>` with a fully
 * styleable, glass-themed option panel.
 *
 * The OPEN panel is rendered via `ReactDOM.createPortal` into
 * `document.body` and positioned with `position: fixed` based on the
 * trigger's `getBoundingClientRect()`. This makes the dropdown a true
 * overlay — it never inflates the trigger's parent height, never pushes
 * neighbouring rows, and is never clipped by `overflow: hidden`,
 * rounded glass shells, table wrappers or filter containers.
 *
 * Behavioural contract (mirrors native `<select>`):
 *  - `value`, `onChange({ target: { value } })`, `disabled`, `className`,
 *    `data-testid`, `<option>` / `<optgroup>` children all preserved.
 *  - Keyboard: ↑ / ↓ / Home / End / Enter / Esc / first-letter typeahead.
 *  - Outside-click + Escape close. Focus returns to trigger on close.
 *  - ARIA: `haspopup`, `expanded`, `activedescendant`, `selected`, `disabled`.
 *  - `prefers-reduced-motion: reduce` removes the spring-in animation
 *    (the keyframe is gated in polish.css).
 *
 * Viewport handling:
 *  - Opens BELOW the trigger by default; flips ABOVE if there isn't
 *    enough room below the viewport.
 *  - Clamps horizontally so it stays inside the viewport.
 *  - Repositions on `scroll` (capture, passive) and `resize`. Repositions
 *    instead of closing because admins on the Pipeline screen frequently
 *    scroll the body while a filter is open.
 */
import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';

const PANEL_VIEWPORT_PADDING = 8;       // keep this much air around the panel
const PANEL_TRIGGER_GAP      = 6;       // between trigger edge and panel
const PANEL_MAX_HEIGHT       = 320;     // matches max-height in polish.css

function flattenOptions(children) {
  const out = [];
  React.Children.forEach(children, (c) => {
    if (!c || typeof c !== 'object') return;
    if (c.type === 'optgroup') {
      out.push({ kind: 'group', label: c.props.label });
      React.Children.forEach(c.props.children, (g) => {
        if (g && typeof g === 'object' && g.type === 'option') {
          out.push({
            kind: 'option', value: g.props.value,
            label: g.props.children, disabled: !!g.props.disabled,
          });
        }
      });
    } else if (c.type === 'option') {
      out.push({
        kind: 'option', value: c.props.value,
        label: c.props.children, disabled: !!c.props.disabled,
      });
    }
  });
  return out;
}

export default function GlassSelect({
  value,
  onChange,
  disabled = false,
  className,
  style,
  children,
  'data-testid': testId,
  placeholder,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [panelStyle, setPanelStyle] = useState(null); // {top, left, width, maxHeight, openUp}

  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  const items = useMemo(() => flattenOptions(children), [children]);
  const optionRows = items.filter((i) => i.kind === 'option');

  const currentLabel = useMemo(() => {
    const found = optionRows.find((o) => String(o.value) === String(value));
    if (found) return found.label;
    return placeholder || (optionRows[0] && optionRows[0].label) || '';
  }, [optionRows, value, placeholder]);

  const fire = useCallback((newVal) => {
    if (typeof onChange === 'function') {
      onChange({ target: { value: newVal } });
    }
  }, [onChange]);

  const choose = useCallback((idx) => {
    const opt = optionRows[idx];
    if (!opt || opt.disabled) return;
    fire(opt.value);
    setOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  }, [optionRows, fire]);

  // ── position calculator ─────────────────────────────────────────────
  const recomputePanelStyle = useCallback(() => {
    const trig = triggerRef.current;
    if (!trig) return;
    const rect = trig.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const triggerWidth = rect.width;
    const minPanelWidth = Math.max(triggerWidth, 180);
    // Room below vs. above the trigger.
    const spaceBelow = vh - rect.bottom - PANEL_TRIGGER_GAP - PANEL_VIEWPORT_PADDING;
    const spaceAbove = rect.top - PANEL_TRIGGER_GAP - PANEL_VIEWPORT_PADDING;

    let openUp = false;
    let maxHeight = Math.min(PANEL_MAX_HEIGHT, Math.max(spaceBelow, 120));
    if (spaceBelow < 200 && spaceAbove > spaceBelow) {
      openUp = true;
      maxHeight = Math.min(PANEL_MAX_HEIGHT, Math.max(spaceAbove, 120));
    }

    // Horizontal clamp.
    let left = rect.left;
    if (left + minPanelWidth + PANEL_VIEWPORT_PADDING > vw) {
      left = Math.max(PANEL_VIEWPORT_PADDING, vw - minPanelWidth - PANEL_VIEWPORT_PADDING);
    }
    if (left < PANEL_VIEWPORT_PADDING) left = PANEL_VIEWPORT_PADDING;

    const top = openUp
      ? Math.max(PANEL_VIEWPORT_PADDING, rect.top - PANEL_TRIGGER_GAP)
      : rect.bottom + PANEL_TRIGGER_GAP;

    setPanelStyle({
      position: 'fixed',
      top: openUp ? undefined : top,
      bottom: openUp ? Math.max(PANEL_VIEWPORT_PADDING, vh - top) : undefined,
      left,
      minWidth: minPanelWidth,
      maxWidth: Math.min(420, vw - 2 * PANEL_VIEWPORT_PADDING),
      maxHeight,
    });
  }, []);

  // Recompute synchronously when the panel opens to avoid the first-frame jump,
  // then transfer DOM focus to the listbox so arrow / typeahead keystrokes are
  // routed to onListKey instead of being swallowed by the still-focused trigger.
  useLayoutEffect(() => {
    if (open) {
      recomputePanelStyle();
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.focus();
      });
    }
  }, [open, recomputePanelStyle]);

  // Reposition (don't close) on scroll/resize while open.
  useEffect(() => {
    if (!open) return undefined;
    const onAny = () => recomputePanelStyle();
    window.addEventListener('scroll', onAny, true);   // capture catches inner scrollers
    window.addEventListener('resize', onAny);
    return () => {
      window.removeEventListener('scroll', onAny, true);
      window.removeEventListener('resize', onAny);
    };
  }, [open, recomputePanelStyle]);

  // Outside-click + Escape. Now the panel lives in `document.body`, so we
  // must allow clicks inside it as well as inside the trigger wrapper.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
      const inList = listRef.current && listRef.current.contains(e.target);
      if (!inWrap && !inList) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Sync activeIdx + scroll-into-view when opening.
  // DELIBERATE single-fire-on-open contract: this effect must run only
  // when `open` transitions to true. We intentionally read `value` and
  // `optionRows` via closure (not deps) because both are recomputed on
  // every render — including the renders triggered by `onMouseEnter`
  // setting activeIdx — and re-running this effect on those renders
  // calls `scrollIntoView` repeatedly, snapping the panel back to the
  // first/selected row while the user is mid-scroll.
  useEffect(() => {
    if (!open) return;
    const idx = optionRows.findIndex((o) => String(o.value) === String(value));
    setActiveIdx(idx >= 0 ? idx : 0);
    setTimeout(() => {
      const el = listRef.current?.querySelector(
        `[data-glass-option-idx="${idx >= 0 ? idx : 0}"]`,
      );
      if (el) el.scrollIntoView({ block: 'nearest' });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onTriggerKey = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onListKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let i = activeIdx;
      for (let n = 0; n < optionRows.length; n += 1) {
        i = (i + 1) % optionRows.length;
        if (!optionRows[i].disabled) { setActiveIdx(i); break; }
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      let i = activeIdx;
      for (let n = 0; n < optionRows.length; n += 1) {
        i = (i - 1 + optionRows.length) % optionRows.length;
        if (!optionRows[i].disabled) { setActiveIdx(i); break; }
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      const i = optionRows.findIndex((o) => !o.disabled);
      if (i >= 0) setActiveIdx(i);
    } else if (e.key === 'End') {
      e.preventDefault();
      for (let i = optionRows.length - 1; i >= 0; i -= 1) {
        if (!optionRows[i].disabled) { setActiveIdx(i); break; }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(activeIdx);
    } else if (e.key.length === 1 && /\S/.test(e.key)) {
      const want = e.key.toLowerCase();
      const start = (activeIdx + 1) % optionRows.length;
      for (let n = 0; n < optionRows.length; n += 1) {
        const i = (start + n) % optionRows.length;
        const label = optionRows[i].label;
        const text = (typeof label === 'string' ? label : '').toLowerCase();
        if (!optionRows[i].disabled && text.startsWith(want)) {
          setActiveIdx(i); break;
        }
      }
    }
  };

  // Render the panel into document.body so it escapes every parent
  // `overflow: hidden`, transformed ancestor, table wrapper, etc.
  const panel = (open && panelStyle) ? (
    <ul
      ref={listRef}
      role="listbox"
      tabIndex={-1}
      onKeyDown={onListKey}
      className="qf-glass-select-panel qf-glass-panel qf-menu-open"
      aria-activedescendant={`glass-opt-${activeIdx}`}
      style={panelStyle}
    >
      {items.map((it, idx) => {
        if (it.kind === 'group') {
          return (
            <li key={`grp-${idx}`} className="qf-glass-select-group">
              {it.label}
            </li>
          );
        }
        const optIdx = optionRows.findIndex((o) => o === it);
        const selected = String(it.value) === String(value);
        const active = optIdx === activeIdx;
        return (
          <li
            key={`opt-${optIdx}-${it.value}`}
            id={`glass-opt-${optIdx}`}
            role="option"
            data-glass-option-idx={optIdx}
            aria-selected={selected}
            aria-disabled={it.disabled || undefined}
            onMouseEnter={() => !it.disabled && setActiveIdx(optIdx)}
            onClick={() => choose(optIdx)}
            className={
              'qf-glass-select-option' +
              (selected ? ' is-selected' : '') +
              (active ? ' is-active' : '') +
              (it.disabled ? ' is-disabled' : '')
            }
          >
            <span className="qf-glass-select-option-label">{it.label}</span>
            {selected && (
              <span aria-hidden className="qf-glass-select-tick">✓</span>
            )}
          </li>
        );
      })}
    </ul>
  ) : null;

  return (
    <div
      ref={wrapRef}
      className={`qf-glass-select ${className || ''}`}
      style={{ position: 'relative', ...(style || {}) }}
    >
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid={testId}
        className="qf-glass-select-trigger"
        {...rest}
      >
        <span className="qf-glass-select-label">{currentLabel}</span>
        <span aria-hidden className="qf-glass-select-chevron" />
      </button>

      {panel && ReactDOM.createPortal(panel, document.body)}
    </div>
  );
}
